import { FC, useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Label, Modal, Select, Spinner, TextInput } from "flowbite-react";
import { HiExclamationCircle, HiLink, HiPlus, HiRefresh, HiSearch } from "react-icons/hi";
import { Navigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "@/lib/toast";
import NavbarSidebarLayout from "@/layouts/navbar-sidebar";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isAdminTierUser } from "@/constants/agent";
import { isApiConnectionsLocked } from "@/constants/company";
import { subdomain } from "@/config/api";
import {
  useConnectIntegrationMutation,
  useDisconnectIntegrationMutation,
  useGetIntegrationProvidersQuery,
  useGetIntegrationsQuery,
  useLazyLookupCustomerQuery,
  type CustomerMatch,
  type Integration,
} from "@/api/integrationApi/integration";

/** One system's slice of a customer lookup answer. */
interface LookupGroup {
  sourceSystem: string;
  matchCount?: number;
  matches?: CustomerMatch[];
  note?: string;
}

/**
 * Every system returns its customer record in its own shape, so the detail is
 * rendered field by field rather than against a schema we would have to invent.
 * Nested objects and arrays are skipped: flattening them here would produce
 * rows nobody can read, and guessing which nested field is "the balance" is
 * exactly the kind of assumption that shows the wrong number.
 */
function customerFields(entry: CustomerMatch): [string, string][] {
  const fields: [string, string][] = [];

  const add = (key: string, value: unknown) => {
    if (value === null || value === undefined || value === "") return;
    if (typeof value === "object") return;
    fields.push([key, String(value)]);
  };

  add("contact_id", entry.match?.contact_id);
  add("phone", entry.match?.phone);
  add("postal_code", entry.match?.postal_code);
  // Skipped because the surrounding UI already says them, and repeating a fact
  // three times makes a reader wonder whether the copies could disagree:
  //   tenantId     - internal, identical on every row
  //   sourceSystem - the group heading above is exactly this
  //   customerName - the bold line above, when it matches
  const heading = (entry.match?.contact_name ?? "").trim();
  for (const [key, value] of Object.entries(entry.customer ?? {})) {
    if (key === "tenantId" || key === "sourceSystem") continue;
    if (key === "customerName" && String(value).trim() === heading) continue;
    add(key, value);
  }
  return fields;
}

/**
 * A document bundle as the adapters return it: an envelope with the records
 * inside. The record fields differ per system and per module, so the table is
 * built from whatever keys the first record carries rather than a fixed set.
 */
function documentRows(bundle: Record<string, unknown> | undefined): {
  note?: string;
  columns: string[];
  rows: Record<string, unknown>[];
} {
  if (!bundle) return { columns: [], rows: [] };
  if (typeof bundle.error === "string") return { note: bundle.error, columns: [], rows: [] };
  if (typeof bundle.note === "string") return { note: bundle.note, columns: [], rows: [] };

  const records = Array.isArray(bundle.records) ? (bundle.records as Record<string, unknown>[]) : [];
  if (records.length === 0) return { columns: [], rows: [] };

  // Only scalar columns: a nested object in a cell would render as [object
  // Object], which tells the reader nothing and hides that data exists.
  const columns = Object.keys(records[0]).filter((key) =>
    records.every((r) => r[key] === null || r[key] === undefined || typeof r[key] !== "object")
  );
  return { columns, rows: records };
}

/** Colour and label per connection state. */
const STATUS_COLOR: Record<Integration["status"], string> = {
  active: "success",
  pending: "warning",
  error: "failure",
  revoked: "gray",
};

const IntegrationList: FC = () => {
  const { t, i18n } = useTranslation(["integration", "common"]);
  const user = useCurrentUser();
  const canManage =
    isAdminTierUser(user);
  // Some tenants (kktcedevlet) do not get external connections yet: the page
  // only states that nothing is connected and offers no way to connect. The
  // tenant is taken from the subdomain the panel is served on, falling back
  // to the company on the session.
  const isLocked =
    isApiConnectionsLocked(subdomain) || isApiConnectionsLocked(user?.company_domain);

  const [searchParams, setSearchParams] = useSearchParams();

  const [showConnect, setShowConnect] = useState(false);
  const [provider, setProvider] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [disconnectTarget, setDisconnectTarget] = useState<Integration | null>(null);
  // Set when the provider came back saying "your account has several
  // organizations, which one?" — carried here from the gateway callback.
  const [choices, setChoices] = useState<{ id: string; name: string }[] | null>(null);
  const [choiceProvider, setChoiceProvider] = useState("");
  const [chosenId, setChosenId] = useState("");
  const [lookupEmail, setLookupEmail] = useState("");

  const {
    data: integrations,
    isLoading,
    isFetching,
    refetch,
  } = useGetIntegrationsQuery(undefined, {
    skip: !canManage || isLocked,
    refetchOnMountOrArgChange: true,
  });
  const { data: providers, refetch: refetchProviders } = useGetIntegrationProvidersQuery(
    undefined,
    { skip: !canManage || isLocked }
  );

  // Refresh means "refresh this page", not "refresh one of its two lists".
  // The provider list carries the display names, so a stale one leaves the
  // cards labelled with raw codes like PARASUT instead of Paraşüt.
  const refreshAll = () => {
    refetch();
    refetchProviders();
  };

  const [lookupCustomer, { data: lookup, error: lookupError, isFetching: isLookingUp }] =
    useLazyLookupCustomerQuery();
  const [connectIntegration, { isLoading: isConnecting }] = useConnectIntegrationMutation();
  const [disconnectIntegration, { isLoading: isDisconnecting }] =
    useDisconnectIntegrationMutation();

  // The gateway sends the browser back here with ?connected=ZOHO once consent
  // went through. Announce it, pull the fresh row, and drop the parameter so a
  // refresh does not replay the message.
  const connected = searchParams.get("connected");
  useEffect(() => {
    if (!connected) return;
    toast.success(t("integration:connectedToast", { provider: connected }));
    // Both refetch handles are stable; refreshAll is not, and putting it in the
    // dependency list would re-run this on every render.
    refetch();
    refetchProviders();
    searchParams.delete("connected");
    setSearchParams(searchParams, { replace: true });
  }, [connected, refetch, refetchProviders, searchParams, setSearchParams, t]);

  const rows = useMemo(() => integrations ?? [], [integrations]);

  // A connection that stopped at "which organization?" carries the candidates
  // on the row itself, so the question is still there after a reload — or after
  // whatever rewrote the address bar on the way back from the provider.
  const awaitingChoice = rows.find((row) => row.pending_choices?.length > 0);

  useEffect(() => {
    if (!awaitingChoice) return;
    setChoices(awaitingChoice.pending_choices);
    setChoiceProvider(awaitingChoice.provider);
  }, [awaitingChoice]);
  const providerOptions = providers ?? [];

  // Already-connected systems are dropped from the picker: the backend keys a
  // connection on (company, provider), so re-picking one would only ever be a
  // re-authorization — which is what "reconnect" on the card is for.
  const connectableProviders = providerOptions.filter(
    (option) => !rows.some((row) => row.provider === option.provider && row.status !== "revoked")
  );

  if (!canManage) {
    return <Navigate to="/" replace />;
  }

  if (isLocked) {
    return (
      <NavbarSidebarLayout>
        <div className="px-4 pt-6">
          <div className="mb-6 flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 text-white shadow-md shadow-teal-500/30">
              <HiLink className="h-6 w-6" />
            </span>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                {t("integration:title")}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("integration:subtitle")}
              </p>
            </div>
          </div>
          <Card>
            <div className="py-10 text-center">
              <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 text-gray-400 dark:bg-gray-700">
                <HiLink className="h-7 w-7" />
              </span>
              <p className="font-medium text-gray-900 dark:text-white">
                {t("integration:lockedTitle")}
              </p>
              <p className="mx-auto mt-1 max-w-md text-sm text-gray-500 dark:text-gray-400">
                {t("integration:lockedBody")}
              </p>
            </div>
          </Card>
        </div>
      </NavbarSidebarLayout>
    );
  }

  const isReauthorizing =
    !!provider && rows.some((row) => row.provider === provider && row.status !== "revoked");

  const labelFor = (value: string) =>
    providerOptions.find((option) => option.provider === value)?.label ?? value;

  const formatDate = (value: string | null) => {
    if (!value) return "—";
    return new Date(value).toLocaleString(i18n.language === "tr" ? "tr-TR" : "en-GB", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const handleConnect = async () => {
    if (!provider) {
      toast.error(t("integration:selectProviderFirst"));
      return;
    }
    if (provider === "PARASUT" && !organizationId.trim()) {
      toast.error(t("integration:organizationIdRequired"));
      return;
    }

    try {
      const res = await connectIntegration({
        provider,
        organization_id: organizationId.trim() || undefined,
      }).unwrap();

      setShowConnect(false);
      setProvider("");
      setOrganizationId("");
      // Same tab, the way OAuth is meant to run: the provider hands the browser
      // back to the gateway, which returns it here. A second tab would strand
      // the result away from the page the user is looking at — and some consent
      // screens refuse to run in a popup at all.
      window.location.assign(res.authorize_url);
    } catch {
      toast.error(t("integration:connectFailed"));
    }
  };

  // Picking an organization restarts the flow with that id pinned, so the
  // provider stops asking. The consent screen appears once more — the previous
  // authorization was never completed into a connection.
  const handleChoose = async () => {
    if (!chosenId) {
      toast.error(t("integration:chooseNone"));
      return;
    }
    try {
      const res = await connectIntegration({
        provider: choiceProvider,
        organization_id: chosenId,
      }).unwrap();
      setChoices(null);
      setChosenId("");
      window.location.assign(res.authorize_url);
    } catch {
      toast.error(t("integration:connectFailed"));
    }
  };

  // The answer arrives either as one system's result or grouped under
  // resultsBySystem when the company is connected to several. Flattening both
  // into one list keeps the rendering honest: records are shown per system and
  // never merged, because two systems' records are not the same record.
  const lookupGroups = useMemo(() => {
    if (!lookup) return [];
    const data = lookup.data as {
      resultsBySystem?: Record<string, LookupGroup>;
    } & LookupGroup;
    return data?.resultsBySystem ? Object.values(data.resultsBySystem) : [data];
  }, [lookup]);

  const lookupErrorMessage = (() => {
    if (!lookupError) return null;
    const body = (lookupError as { data?: { message?: string; error?: string } }).data;
    return body?.message || body?.error || t("integration:lookupFailed");
  })();

  const handleLookup = () => {
    const email = lookupEmail.trim();
    if (!email) {
      toast.error(t("integration:lookupEmailRequired"));
      return;
    }
    lookupCustomer(email);
  };

  const handleDisconnect = async () => {
    if (!disconnectTarget) return;
    try {
      await disconnectIntegration(disconnectTarget.id).unwrap();
      toast.success(t("integration:disconnected", { provider: labelFor(disconnectTarget.provider) }));
      setDisconnectTarget(null);
    } catch {
      toast.error(t("integration:disconnectFailed"));
    }
  };

  return (
    <NavbarSidebarLayout>
      <div className="px-4 pt-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 text-white shadow-md shadow-teal-500/30">
              <HiLink className="h-6 w-6" />
            </span>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                {t("integration:title")}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("integration:subtitle")}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button color="light" onClick={refreshAll} disabled={isFetching}>
              <HiRefresh className={`mr-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
              {t("common:refresh")}
            </Button>
            {/* Disabled only when we know there is nothing left to connect.
                An empty providerOptions means the list has not arrived (or
                failed) — killing the primary action over that would leave the
                user with no way in and no explanation. */}
            <Button
              onClick={() => setShowConnect(true)}
              disabled={providerOptions.length > 0 && connectableProviders.length === 0}
            >
              <HiPlus className="mr-2 h-4 w-4" />
              {t("integration:connect")}
            </Button>
          </div>
        </div>

        {isLoading ? (
          <Card>
            <div className="flex justify-center py-12">
              <Spinner size="xl" />
            </div>
          </Card>
        ) : rows.length === 0 ? (
          <Card>
            <div className="py-10 text-center">
              <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 text-gray-400 dark:bg-gray-700">
                <HiLink className="h-7 w-7" />
              </span>
              <p className="font-medium text-gray-900 dark:text-white">
                {t("integration:emptyTitle")}
              </p>
              <p className="mx-auto mt-1 max-w-md text-sm text-gray-500 dark:text-gray-400">
                {t("integration:emptyBody")}
              </p>
              <Button className="mx-auto mt-5" onClick={() => setShowConnect(true)}>
                <HiPlus className="mr-2 h-4 w-4" />
                {t("integration:connect")}
              </Button>
            </div>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {rows.map((row) => (
              // Flowbite centres card content vertically by default, which
              // leaves the titles of cards of different heights out of line.
              <Card
                key={row.id}
                theme={{ root: { children: "flex h-full flex-col justify-start gap-4 p-6" } }}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                      {labelFor(row.provider)}
                    </h2>
                    <p className="text-xs uppercase tracking-wide text-gray-400">
                      {row.provider_kind === "crm"
                        ? t("integration:kind.crm")
                        : t("integration:kind.accounting")}
                    </p>
                  </div>
                  <Badge color={STATUS_COLOR[row.status]} className="w-fit shrink-0">
                    {t(`integration:status.${row.status}`)}
                  </Badge>
                </div>

                {row.status === "pending" && (
                  <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                    {t("integration:pendingHint")}
                  </p>
                )}

                {row.status === "error" && (
                  <p className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-xs text-red-800 dark:bg-red-900/30 dark:text-red-200">
                    <HiExclamationCircle className="mt-px h-4 w-4 shrink-0" />
                    <span>
                      {t("integration:errorHint")}
                      {row.last_error ? ` (${row.last_error})` : ""}
                    </span>
                  </p>
                )}

                <dl className="space-y-1 text-sm">
                  <div className="flex justify-between gap-2">
                    <dt className="text-gray-500 dark:text-gray-400">
                      {t("integration:field.connectedAt")}
                    </dt>
                    <dd className="text-gray-900 dark:text-white">{formatDate(row.connected_at)}</dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-gray-500 dark:text-gray-400">
                      {t("integration:field.lastUsed")}
                    </dt>
                    <dd className="text-gray-900 dark:text-white">{formatDate(row.last_used_at)}</dd>
                  </div>
                  {row.organization_id && (
                    <div className="flex justify-between gap-2">
                      <dt className="text-gray-500 dark:text-gray-400">
                        {t("integration:field.organization")}
                      </dt>
                      <dd className="truncate text-gray-900 dark:text-white">
                        {row.organization_id}
                      </dd>
                    </div>
                  )}
                  {row.partner_api_key_hint && (
                    <div className="flex justify-between gap-2">
                      <dt className="text-gray-500 dark:text-gray-400">
                        {t("integration:field.apiKey")}
                      </dt>
                      <dd className="font-mono text-gray-900 dark:text-white">
                        {row.partner_api_key_hint}
                      </dd>
                    </div>
                  )}
                </dl>

                <div className="flex items-center gap-2">
                  <Button
                    size="xs"
                    color="light"
                    onClick={() => {
                      setProvider(row.provider);
                      setOrganizationId(row.organization_id ?? "");
                      setShowConnect(true);
                    }}
                  >
                    {row.status === "active"
                      ? t("integration:reauthorize")
                      : t("integration:retry")}
                  </Button>
                  {row.status !== "revoked" && (
                    <Button size="xs" color="failure" onClick={() => setDisconnectTarget(row)}>
                      {t("integration:disconnect")}
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Look a customer up by address — the reason the connection exists.
            Hidden until something is actually connected: an empty search box
            over no data would only invite the question "why does this fail?" */}
        {rows.some((row) => row.status === "active") && (
          <Card className="mt-6">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-50 text-teal-600 dark:bg-teal-900/30 dark:text-teal-300">
                <HiSearch className="h-5 w-5" />
              </span>
              <div>
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                  {t("integration:lookupTitle")}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t("integration:lookupSubtitle")}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-end gap-2">
              {/* Capped: a lone email field stretched across a wide screen
                  reads as a mistake, not as an invitation to type. */}
              <div className="min-w-[240px] max-w-sm flex-1">
                <Label htmlFor="lookup-email">{t("integration:lookupEmail")}</Label>
                <TextInput
                  id="lookup-email"
                  type="email"
                  placeholder="musteri@firma.com"
                  value={lookupEmail}
                  onChange={(e) => setLookupEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleLookup();
                  }}
                />
              </div>
              <Button onClick={handleLookup} disabled={isLookingUp}>
                {isLookingUp ? <Spinner size="sm" /> : t("integration:lookupAction")}
              </Button>
            </div>

            {lookupErrorMessage && (
              <p className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/30 dark:text-red-200">
                <HiExclamationCircle className="mt-px h-4 w-4 shrink-0" />
                <span>{lookupErrorMessage}</span>
              </p>
            )}

            {lookupGroups.map((group) => (
              <div key={group.sourceSystem} className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {labelFor(group.sourceSystem)}
                  </span>
                  <Badge color={group.matches?.length ? "success" : "gray"} className="w-fit">
                    {t("integration:lookupMatches", { count: group.matches?.length ?? 0 })}
                  </Badge>
                </div>

                {!group.matches?.length ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {group.note || t("integration:lookupNoMatch")}
                  </p>
                ) : (
                  <div className="flex flex-col gap-3">
                    {group.matches.map((entry: CustomerMatch, i: number) => (
                      <div key={entry.match?.contact_id ?? i} className="text-sm">
                        <p className="font-medium text-gray-900 dark:text-white">
                          {entry.match?.contact_name || t("integration:lookupUnnamed")}
                        </p>
                        <dl className="mt-1 grid gap-x-6 gap-y-1 sm:grid-cols-2">
                          {customerFields(entry).map(([key, value]) => (
                            <div key={key} className="flex justify-between gap-2 border-b border-gray-100 py-1 dark:border-gray-800">
                              <dt className="text-gray-500 dark:text-gray-400">{key}</dt>
                              <dd className="text-right text-gray-900 dark:text-white">{value}</dd>
                            </div>
                          ))}
                        </dl>

                        {Object.entries(entry.documents ?? {}).map(([type, bundle]) => {
                          const { note, columns, rows: records } = documentRows(bundle);
                          return (
                            <div key={type} className="mt-3">
                              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
                                {t(`integration:doc.${type}`, type)}
                                {records.length > 0 && ` (${records.length})`}
                              </h4>
                              {note ? (
                                <p className="text-xs text-gray-500 dark:text-gray-400">{note}</p>
                              ) : records.length === 0 ? (
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {t("integration:doc.empty")}
                                </p>
                              ) : (
                                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                                  <table className="w-full text-xs">
                                    <thead className="bg-gray-50 dark:bg-gray-800">
                                      <tr>
                                        {columns.map((c) => (
                                          <th key={c} className="whitespace-nowrap px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">
                                            {c}
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {records.map((rec, ri) => (
                                        <tr key={ri} className="border-t border-gray-100 dark:border-gray-800">
                                          {columns.map((c) => (
                                            <td key={c} className="whitespace-nowrap px-3 py-2 text-gray-900 dark:text-white">
                                              {rec[c] === null || rec[c] === undefined ? "—" : String(rec[c])}
                                            </td>
                                          ))}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </Card>
        )}

      </div>

      {/* Pick a system */}
      <Modal show={showConnect} onClose={() => setShowConnect(false)}>
        <Modal.Header>
          {/* The same dialog serves both flows; saying "connect" while the user
              is re-authorizing an existing system reads as the wrong action. */}
          {isReauthorizing ? t("integration:reauthorizeTitle") : t("integration:connectTitle")}
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t("integration:connectHelp")}
            </p>
            {providerOptions.length === 0 && (
              <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                {t("integration:providersUnavailable")}
              </p>
            )}
            <div>
              <Label htmlFor="provider">{t("integration:form.provider")}</Label>
              <Select
                id="provider"
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
              >
                <option value="">{t("integration:form.selectProvider")}</option>
                {/* A re-authorization targets a system already in the list, so
                    the current pick stays selectable even when connected. */}
                {providerOptions
                  .filter(
                    (option) =>
                      option.provider === provider ||
                      connectableProviders.some((c) => c.provider === option.provider)
                  )
                  .map((option) => (
                    <option key={option.provider} value={option.provider}>
                      {option.label}
                    </option>
                  ))}
              </Select>
            </div>
            {provider === "PARASUT" && (
              <div>
                <Label htmlFor="organization">{t("integration:form.organizationId")}</Label>
                <TextInput
                  id="organization"
                  value={organizationId}
                  onChange={(e) => setOrganizationId(e.target.value)}
                  placeholder="123456"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t("integration:form.organizationIdHelp")}
                </p>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleConnect} disabled={isConnecting}>
            {isConnecting ? <Spinner size="sm" /> : t("integration:continue")}
          </Button>
          <Button color="gray" onClick={() => setShowConnect(false)}>
            {t("common:cancel")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Which organization? — asked by name, never by id */}
      <Modal show={!!choices} onClose={() => setChoices(null)} size="md">
        <Modal.Header>{t("integration:chooseTitle")}</Modal.Header>
        <Modal.Body>
          <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
            {t("integration:chooseBody", { provider: labelFor(choiceProvider) })}
          </p>
          <div className="flex flex-col gap-2">
            {(choices ?? []).map((option) => (
              <label
                key={option.id}
                className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 text-sm ${
                  chosenId === option.id
                    ? "border-teal-500 bg-teal-50 dark:bg-teal-900/20"
                    : "border-gray-200 dark:border-gray-700"
                }`}
              >
                <input
                  type="radio"
                  name="organization"
                  value={option.id}
                  checked={chosenId === option.id}
                  onChange={() => setChosenId(option.id)}
                  className="h-4 w-4 text-teal-600"
                />
                <span>
                  <span className="block font-medium text-gray-900 dark:text-white">
                    {option.name || option.id}
                  </span>
                  <span className="block font-mono text-xs text-gray-400">{option.id}</span>
                </span>
              </label>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleChoose} disabled={isConnecting}>
            {isConnecting ? <Spinner size="sm" /> : t("integration:chooseConfirm")}
          </Button>
          <Button color="gray" onClick={() => setChoices(null)}>
            {t("common:cancel")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Disconnect confirm */}
      <Modal
        show={!!disconnectTarget}
        onClose={() => setDisconnectTarget(null)}
        size="md"
      >
        <Modal.Header>{t("integration:disconnectTitle")}</Modal.Header>
        <Modal.Body>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {t("integration:disconnectConfirm", {
              provider: disconnectTarget ? labelFor(disconnectTarget.provider) : "",
            })}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button color="failure" onClick={handleDisconnect} disabled={isDisconnecting}>
            {isDisconnecting ? <Spinner size="sm" /> : t("integration:disconnect")}
          </Button>
          <Button color="gray" onClick={() => setDisconnectTarget(null)}>
            {t("common:cancel")}
          </Button>
        </Modal.Footer>
      </Modal>
    </NavbarSidebarLayout>
  );
};

export default IntegrationList;
